import type { VisemeCue, VisemeId } from '../avatar/visemes.js';

/**
 * Espanol escrito -> visemas.
 *
 * El espanol es casi fonetico, asi que la correspondencia entre letras y
 * sonidos se resuelve con un puñado de reglas deterministas y sin diccionario.
 * Eso es lo que hace viable la sincronizacion labial con un avatar propio: no
 * hace falta un motor de fonemas externo.
 *
 * Dos formas de generar la linea de tiempo:
 *
 *   textToCues(texto, duracionMs)
 *     Estimacion por duracion total. Se usa en el Hito 1, cuando todavia no hay
 *     audio real, y para el video de presentacion.
 *
 *   alignmentToCues(chars, inicios, duraciones)
 *     Marcas de tiempo reales por caracter, como las devuelve ElevenLabs con
 *     sync_alignment. Es lo que se usa en el Hito 2: no acumula desfase aunque
 *     la frase sea larga.
 *
 * Ambas devuelven la misma estructura, asi que el avatar no distingue entre una
 * y otra.
 */

/** Duracion relativa de cada visema. Las vocales duran mas que las oclusivas. */
const WEIGHT: Record<VisemeId, number> = {
  REST: 1.0,
  A: 1.35,
  E: 1.25,
  I: 1.1,
  O: 1.3,
  U: 1.15,
  MBP: 0.75,
  FV: 0.85,
  TD: 0.8,
  CH: 0.95,
  K: 0.8,
  RR: 0.95,
};

const VOWELS: Record<string, VisemeId> = {
  a: 'A',
  á: 'A',
  e: 'E',
  é: 'E',
  i: 'I',
  í: 'I',
  y: 'I', // "y" aislada suena como i
  o: 'O',
  ó: 'O',
  u: 'U',
  ú: 'U',
  ü: 'U',
};

/** Un caracter de entrada mapeado a un visema (o a ninguno, si es mudo). */
export interface PhonemeHit {
  /** Indice del primer caracter consumido en el texto original. */
  index: number;
  /** Cuantos caracteres consume (2 en "ch", "ll", "rr", "qu"). */
  length: number;
  viseme: VisemeId | null;
}

/**
 * Recorre el texto y devuelve la secuencia de visemas con la posicion exacta
 * de cada uno en la cadena original. Esa posicion es la que permite cruzarlos
 * despues con las marcas de tiempo por caracter del sintetizador.
 */
export function scan(text: string): PhonemeHit[] {
  const lower = text.toLowerCase();
  const hits: PhonemeHit[] = [];
  let i = 0;

  const isVowel = (c: string | undefined) => c !== undefined && c in VOWELS;
  const isFrontVowel = (c: string | undefined) => c === 'e' || c === 'i' || c === 'é' || c === 'í';

  while (i < lower.length) {
    const c = lower[i]!;
    const next = lower[i + 1];

    // --- digrafos -----------------------------------------------------------
    if (c === 'c' && next === 'h') {
      hits.push({ index: i, length: 2, viseme: 'CH' });
      i += 2;
      continue;
    }
    if (c === 'l' && next === 'l') {
      hits.push({ index: i, length: 2, viseme: 'CH' }); // yeismo
      i += 2;
      continue;
    }
    if (c === 'r' && next === 'r') {
      hits.push({ index: i, length: 2, viseme: 'RR' });
      i += 2;
      continue;
    }
    if (c === 'q' && next === 'u') {
      // "que", "qui": la u es muda.
      hits.push({ index: i, length: 2, viseme: 'K' });
      i += 2;
      continue;
    }
    if (c === 'g' && next === 'u' && isFrontVowel(lower[i + 2])) {
      // "gue", "gui": la u es muda.
      hits.push({ index: i, length: 2, viseme: 'K' });
      i += 2;
      continue;
    }

    // --- letras sueltas -----------------------------------------------------
    if (isVowel(c)) {
      hits.push({ index: i, length: 1, viseme: VOWELS[c]! });
      i += 1;
      continue;
    }

    let viseme: VisemeId | null;
    switch (c) {
      case 'h':
        viseme = null; // muda
        break;
      case 'm':
      case 'b':
      case 'p':
      case 'v':
      case 'w':
        viseme = 'MBP';
        break;
      case 'f':
        viseme = 'FV';
        break;
      case 'c':
        viseme = isFrontVowel(next) ? 'TD' : 'K'; // "ce/ci" suenan /s/
        break;
      case 'g':
        viseme = isFrontVowel(next) ? 'K' : 'K'; // jota o velar: mismo visema
        break;
      case 'j':
      case 'x':
      case 'k':
        viseme = 'K';
        break;
      case 'ñ':
        viseme = 'CH';
        break;
      case 't':
      case 'd':
      case 'n':
      case 'l':
      case 's':
      case 'z':
      case 'r':
        viseme = 'TD';
        break;
      default:
        // Solo la puntuacion devuelve la boca al reposo. El ESPACIO no: el
        // espanol resilabifica entre palabras ("los ojos" se pronuncia
        // encadenado), asi que cerrar la boca en cada espacio produce unos dos
        // cierres completos por segundo y es lo que hace que el avatar
        // parezca una marioneta masticando.
        viseme = /[.,;:!?¡¿…—]/.test(c) ? 'REST' : null;
    }

    hits.push({ index: i, length: 1, viseme });
    i += 1;
  }

  return hits;
}

/**
 * Linea de tiempo estimada: reparte la duracion total entre los visemas segun
 * su peso relativo. Precisa para frases cortas, suficiente para el demo.
 */
export function textToCues(text: string, totalMs: number): VisemeCue[] {
  const hits = scan(text).filter((h): h is PhonemeHit & { viseme: VisemeId } => h.viseme !== null);
  if (hits.length === 0) return [];

  const totalWeight = hits.reduce((sum, h) => sum + WEIGHT[h.viseme], 0);
  const unit = totalMs / totalWeight;

  let at = 0;
  return hits.map((hit) => {
    const duration = WEIGHT[hit.viseme] * unit;
    const cue: VisemeCue = { at, duration, id: hit.viseme };
    at += duration;
    return cue;
  });
}

/**
 * Linea de tiempo exacta a partir de las marcas por caracter del sintetizador.
 *
 * @param chars      caracteres tal como los devuelve el motor de voz
 * @param startsMs   inicio de cada caracter, en milisegundos
 * @param durationsMs duracion de cada caracter, en milisegundos
 */
export function alignmentToCues(
  chars: string[],
  startsMs: number[],
  durationsMs: number[],
): VisemeCue[] {
  const text = chars.join('');
  const cues: VisemeCue[] = [];

  for (const hit of scan(text)) {
    if (hit.viseme === null) continue;
    const start = startsMs[hit.index];
    if (start === undefined) continue;

    // Un digrafo ocupa dos caracteres: se suman sus duraciones.
    let duration = 0;
    for (let k = 0; k < hit.length; k += 1) {
      duration += durationsMs[hit.index + k] ?? 0;
    }

    cues.push({ at: start, duration: Math.max(duration, 30), id: hit.viseme });
  }

  return cues;
}

/** Devuelve el visema vigente en un instante dado. Busqueda binaria. */
export function visemeAt(cues: VisemeCue[], timeMs: number): VisemeId {
  if (cues.length === 0) return 'REST';

  let low = 0;
  let high = cues.length - 1;
  let found = -1;

  while (low <= high) {
    const mid = (low + high) >> 1;
    const cue = cues[mid]!;
    if (cue.at <= timeMs) {
      found = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  if (found === -1) return 'REST';
  const cue = cues[found]!;
  // Si el ultimo visema ya termino, la boca vuelve al reposo.
  return timeMs <= cue.at + cue.duration + 60 ? cue.id : 'REST';
}
